import { WorkflowRole } from '@exam-management/common/types';
import {
  type WorkflowStep,
  type WorkflowStepCode,
  QuestionGroupType,
  useGetDownloadSignedUrlForQuestionsByQSetIdQuery,
  useGetQSetByTaskIdQuery,
  useGetReviewQSetSubtaskDetailsByIdQuery,
  useSearchTaskReviewedQSetQuestionsLazyQuery,
} from '@exam-management/graphql';
import { getWorkflowRoleForSubtask } from '@exam-management/task-management';
import { useAuth } from '@shared/auth';
import { ErrorBox, Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Question, SubtaskDetailsData } from '../../types';
import { mapSignedUrlToImages, transformQSetReviewSubtaskDetailsData } from '../../utils';

interface QSetReviewSubtaskContextProps extends PropsWithChildren {
  subtaskId: string;
}

export enum QSetReviewSubtaskDetailsModalAction {
  OVERDUE_NOTICE = 'OVERDUE_NOTICE',
}

interface QSetReviewSubtaskContext {
  subtaskDetails: SubtaskDetailsData;
  status: {
    name: string;
    code: WorkflowStepCode;
  };
  roles: {
    isOwner: boolean;
    isAssigner: boolean;
    isAssignee: boolean;
  };
  qSetQuestion: {
    currentQuestionNumber: number;
    currentQuestion?: Question;
    questions: Question[];
  };
  setCurrentQuestionNumber: (questionNumber: number) => void;
}

export const QSetReviewSubtaskContext = createContext<QSetReviewSubtaskContext | null>(null);

export default function QSetReviewSubtaskProvider(props: Readonly<QSetReviewSubtaskContextProps>) {
  const { subtaskId, children } = props;
  const { userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const order = Number(searchParams.get('order') ?? 1);

  const [
    searchQSetQuestions,
    {
      data: searchedQSetQuestions,
      loading: isSearchingQSetQuestions,
      error: isErrorQSetQuestions,
      called: isCalledQSetQuestions,
    },
  ] = useSearchTaskReviewedQSetQuestionsLazyQuery();

  const {
    data: getQsetByTaskId,
    error: isErrorGetQSetByTaskId,
    loading: isLoadingGetQSetByTaskId,
  } = useGetQSetByTaskIdQuery({
    variables: {
      input: {
        taskId: subtaskId,
      },
    },
  });

  const { data, error: isErrorQSetReviewSubTaskDetail } = useGetReviewQSetSubtaskDetailsByIdQuery({
    onCompleted: (data) => {
      const qSetTaskDetails =
        data?.wflGetTaskById.task.metadata.__typename === 'TaskMetadataWithQSet'
          ? data.wflGetTaskById.task.metadata
          : undefined;
      if (qSetTaskDetails) {
        searchQSetQuestions({
          variables: {
            input: {
              taskId: subtaskId,
              qSetId: qSetTaskDetails.qSet.id,
              paging: {
                pageSize: 200, // NOTED: Get all questions
                currentPage: 0,
              },
            },
          },
        });
      }
    },
    variables: {
      getTaskByIdInput: {
        taskId: subtaskId,
      },
    },
  });

  const {
    data: getDownloadSignedUrlForQuestionsByQSetId,
    loading: isGettingDownloadSignedUrlForQuestionsByQSetId,
  } = useGetDownloadSignedUrlForQuestionsByQSetIdQuery({
    variables: {
      input: {
        qSetId:
          data?.wflGetTaskById.task.metadata.__typename === 'TaskMetadataWithQSet'
            ? data.wflGetTaskById.task.metadata.qSet.id
            : '',
        taskId: subtaskId,
      },
    },
    skip: !data?.wflGetTaskById.task,
  });

  const signedUrls: Record<
    string,
    Record<string, string>
  > = getDownloadSignedUrlForQuestionsByQSetId?.excGetDownloadSignedUrlForQuestionsByQSetId
    .signedUrls;

  const [isOwner, isAssigner, isAssignee] = useMemo(() => {
    if (!data) return [false, false, false];
    const workflowRoles = getWorkflowRoleForSubtask({
      userId: userProfile?.id ?? '',
      assigneeId: data?.wflGetTaskById.task.assignee.id ?? '',
      assignerId: data?.wflGetTaskById.task.assignerId ?? '',
      parentOwnerId: data?.wflGetTaskById.task.parentTask?.ownerId ?? '',
    });

    const [isOwner, isAssigner, isAssignee] = workflowRoles.reduce(
      (acc, role) => [
        acc[0] || role === WorkflowRole.TASK_OWNER,
        acc[1] || role === WorkflowRole.SUBTASK_ASSIGNER,
        acc[2] || role === WorkflowRole.SUBTASK_ASSIGNEE,
      ],
      [false, false, false],
    );
    return [isOwner, isAssigner, isAssignee];
  }, [data, userProfile]);

  const qSetQuestions = useMemo(() => {
    if (!searchedQSetQuestions?.excSearchTaskQSetQuestions?.qSetQuestions || !signedUrls) {
      return [];
    }

    return searchedQSetQuestions?.excSearchTaskQSetQuestions?.qSetQuestions
      .filter(
        // NOTE: We did some TypeScript magic here to make sure that the question is not null.
        // Since TypeScript doesn't know that the question is not null, we have to do this.
        (
          qSetQuestion,
        ): qSetQuestion is typeof qSetQuestion & {
          question: NonNullable<typeof qSetQuestion.question>;
        } => !!qSetQuestion.question,
      )
      .sort((a, b) => a.order - b.order)
      .map((qSetQuestion) => {
        return {
          ...qSetQuestion.question,
          details: mapSignedUrlToImages(
            qSetQuestion.question.details,
            signedUrls[qSetQuestion.questionId],
          ),
          solution: {
            type: qSetQuestion.question.solution?.type ?? null,
            value: mapSignedUrlToImages(
              qSetQuestion.question.solution?.value ?? '',
              signedUrls[qSetQuestion.questionId],
            ),
          },
          metadata: {
            ...qSetQuestion.question.metadata,
            choices: qSetQuestion.question.metadata.choices.map((choice) => ({
              ...choice,
              label: mapSignedUrlToImages(choice.label, signedUrls[qSetQuestion.questionId]),
            })),
          },
          questionGroup: {
            ...qSetQuestion.question.questionGroup,
            type: qSetQuestion.question.questionGroup?.type ?? QuestionGroupType.ItemSet,
            refCode: qSetQuestion.question.questionGroup?.refCode ?? '',
            size: qSetQuestion.question.questionGroup?.size ?? null,
            metadata: {
              ...qSetQuestion.question.questionGroup?.metadata,
              paragraph: mapSignedUrlToImages(
                qSetQuestion.question.questionGroup?.metadata?.paragraph ?? '',
                signedUrls[qSetQuestion.questionGroupId],
              ),
            },
          },
        };
      });
  }, [searchedQSetQuestions, signedUrls]);

  const currentQuestion = useMemo(() => {
    const qNo = Number(order);
    if (isNaN(qNo) || !qSetQuestions[qNo - 1]) return;
    return qSetQuestions[qNo - 1];
  }, [qSetQuestions, order]);

  const handleChangeCurrentQuestionNumber = (questionNumber: number) => {
    const currentQuestionNumber =
      questionNumber > qSetQuestions.length ? qSetQuestions.length : questionNumber;
    setSearchParams(
      { order: currentQuestionNumber === 0 ? '1' : currentQuestionNumber.toString() },
      { replace: true },
    );
    document.getElementById('review-qset-exam-details')?.scrollIntoView();
  };

  const contextValue = useMemo(() => {
    return {
      subtaskDetails: transformQSetReviewSubtaskDetailsData({
        subTask: data?.wflGetTaskById.task,
        qSet: getQsetByTaskId?.excGetQSetByTaskId.qSet,
      }),
      status: {
        name: data?.wflGetTaskById.task.currentWorkflowStep.name,
        code: `${data?.wflGetTaskById.task.currentWorkflowStep.code}`,
      } as WorkflowStep,
      roles: { isOwner, isAssigner, isAssignee },
      qSetQuestion: {
        currentQuestionNumber: order,
        currentQuestion,
        questions: qSetQuestions,
      },
      setCurrentQuestionNumber: handleChangeCurrentQuestionNumber,
    };
  }, [
    isOwner,
    isAssigner,
    isAssignee,
    data,
    currentQuestion,
    getQsetByTaskId,
    qSetQuestions,
    order,
  ]);
  useEffect(() => {
    if (isNaN(order) || order <= 0 || order > qSetQuestions.length) {
      handleChangeCurrentQuestionNumber(qSetQuestions.length > 0 || order === 0 ? 1 : order);
    }
  }, []);

  if (
    isSearchingQSetQuestions ||
    !isCalledQSetQuestions ||
    isGettingDownloadSignedUrlForQuestionsByQSetId ||
    isLoadingGetQSetByTaskId
  ) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (
    isErrorQSetReviewSubTaskDetail ||
    isErrorGetQSetByTaskId ||
    !getQsetByTaskId?.excGetQSetByTaskId.qSet
  ) {
    return <ErrorBox />;
  }

  if (isErrorQSetQuestions && isCalledQSetQuestions) {
    return <ErrorBox />;
  }

  return (
    <QSetReviewSubtaskContext.Provider value={contextValue}>
      {children}
    </QSetReviewSubtaskContext.Provider>
  );
}
